/**
 * Warehouse Shelf Map Report Template
 *
 * Issue: #218 – CORE – Warehouse Shelf Map – Backend Report Runner + Template Registration
 *
 * Produces a letter landscape HTML report showing a warehouse's shelf map.
 * Supports two modes:
 *   - map-id mode: render all slots in a layout
 *   - multi-location mode: render only selected location slots
 */

import { getReportFooterText } from '../reportFooter';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShelfMapReportSlot {
  aisleLabel: string;
  shelfNumber: number;
  columnNumber: number;
  locationName: string | null;
  lin: string;
  nsn: string;
  nomenclature: string;
  quantity: number;
  labelOverride: string | null;
}

export interface ShelfMapReportOptions {
  generatedAt: Date;
  layoutName: string;
  warehouseName: string | null;
  aisleFilter?: string | null;
  rowFilter?: number | null;
  selectedLocationIds?: string[];
  slots: ShelfMapReportSlot[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const escHtml = (value: string | null | undefined): string => {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const safe = (value: string | null | undefined): string => escHtml(value) || '—';

type SlotGroup = {
  aisle: string;
  shelf: number;
  slots: ShelfMapReportSlot[];
};

function groupSlots(slots: ShelfMapReportSlot[]): SlotGroup[] {
  const map = new Map<string, SlotGroup>();
  for (const slot of slots) {
    const key = `${slot.aisleLabel}|${slot.shelfNumber}`;
    if (!map.has(key)) {
      map.set(key, { aisle: slot.aisleLabel, shelf: slot.shelfNumber, slots: [] });
    }
    map.get(key)!.slots.push(slot);
  }
  return [...map.values()].sort((a, b) => {
    const aDiff = a.aisle.localeCompare(b.aisle);
    return aDiff !== 0 ? aDiff : a.shelf - b.shelf;
  });
}

// ---------------------------------------------------------------------------
// HTML builder
// ---------------------------------------------------------------------------

export function buildWarehouseShelfMapReportHtml(options: ShelfMapReportOptions): string {
  const { generatedAt, layoutName, warehouseName, aisleFilter, rowFilter, slots } = options;

  const filters: string[] = [];
  if (aisleFilter) filters.push(`Aisle: ${escHtml(aisleFilter)}`);
  if (rowFilter != null) filters.push(`Shelf Row: ${rowFilter}`);
  if (options.selectedLocationIds && options.selectedLocationIds.length > 0) {
    filters.push(`Selected locations: ${options.selectedLocationIds.length}`);
  }

  const groups = groupSlots(slots);

  const generatedAtStr = generatedAt.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Shelf Map Report – ${escHtml(layoutName)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 16px;
    font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    color: #15263c;
    background: #ffffff;
    font-size: 14px;
  }
  header {
    margin-bottom: 24px;
  }
  .brand {
    font-size: 14px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #3a82f6;
    font-weight: 600;
  }
  .title {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
    color: #15263c;
  }
  .generated {
    margin-top: 4px;
    font-size: 14px;
    color: #4a5c72;
  }
  .filters {
    margin-top: 6px;
    font-size: 13px;
    color: #1565c0;
  }
  .group-section {
    margin-bottom: 24px;
  }
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid #d7e3f4;
  }
  thead th {
    background: #e9f1ff;
    text-align: left;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 10px 12px;
    color: #4a5c72;
    border-bottom: 1px solid #d7e3f4;
  }
  thead th.right { text-align: right; }
  tbody td {
    padding: 10px 12px;
    border-bottom: 1px solid #edf3fc;
    font-size: 13px;
    color: #1f2f45;
    overflow-wrap: break-word;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:nth-child(even) td { background: #f9fbff; }
  .mono { font-family: "Courier New", Courier, monospace; font-size: 12px; }
  .nowrap { white-space: nowrap; }
  .right { text-align: right; }
  .empty { text-align: center; font-style: italic; color: #6b7c92; padding: 24px 12px; }
  .group-title-row td {
    background: #e9f1ff;
    border-bottom: 1px solid #d7e3f4;
    padding: 10px 12px;
  }
  .group-title-cell {
    font-size: 14px;
    font-weight: 700;
    color: #15263c;
  }
  .group-title-label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #5c6f85;
    margin-right: 8px;
  }
  footer {
    margin-top: 32px;
    font-size: 12px;
    color: #5c6f85;
    text-align: center;
    border-top: 1px solid #e1e8f5;
    padding-top: 8px;
  }
  @media print {
    body { margin: 8mm; font-size: 10pt; }
    @page { size: letter landscape; margin: 0.5in; }
    thead { display: table-header-group; }
    thead th { font-size: 9px; padding: 4px 6px; }
    .group-title-row td { font-size: 9px; padding: 4px 6px; }
    tbody td { font-size: 9px; padding: 4px 6px; }
  }
</style>
</head>
<body>

<header>
  <div class="brand">eQuip Transaction Suite</div>
  <h1 class="title">Warehouse Shelf Map Report</h1>
  <div class="generated">${escHtml(layoutName)}${warehouseName ? ` — ${escHtml(warehouseName)}` : ''} &nbsp;·&nbsp; Generated: ${generatedAtStr}</div>
  ${filters.length > 0 ? `<div class="filters">Filters: ${filters.join(' | ')}</div>` : ''}
</header>

${
  groups.length === 0
    ? `<table><thead><tr>
        <th style="width:90px">Slot</th>
        <th style="width:14%">Location / Label</th>
        <th style="width:80px">LIN</th>
        <th class="nowrap" style="width:160px">NSN</th>
        <th>Nomenclature</th>
        <th class="right" style="width:55px">Qty</th>
      </tr></thead>
      <tbody><tr><td colspan="6" class="empty">No slots match the selected filters.</td></tr></tbody>
      </table>`
    : groups
        .map(
          (group) =>
            `<div class="group-section">
              <table>
                <thead>
                  <tr class="group-title-row">
                    <td colspan="6" class="group-title-cell">
                      <span class="group-title-label">Aisle</span>${escHtml(group.aisle)} — Shelf Row ${group.shelf}
                    </td>
                  </tr>
                  <tr>
                    <th style="width:90px">Slot</th>
                    <th style="width:14%">Location / Label</th>
                    <th style="width:80px">LIN</th>
                    <th class="nowrap" style="width:160px">NSN</th>
                    <th>Nomenclature</th>
                    <th class="right" style="width:55px">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  ${group.slots
                    .sort((a, b) => a.columnNumber - b.columnNumber)
                    .map(
                      (s) => `
                  <tr>
                    <td>${escHtml(group.aisle)}-${group.shelf}-${s.columnNumber}</td>
                    <td>${safe(s.locationName ?? s.labelOverride)}</td>
                    <td class="mono">${safe(s.lin)}</td>
                    <td class="mono nowrap">${safe(s.nsn)}</td>
                    <td>${safe(s.nomenclature)}</td>
                    <td class="right">${s.quantity > 0 ? s.quantity : '—'}</td>
                  </tr>`,
                    )
                    .join('')}
                </tbody>
              </table>
            </div>`,
        )
        .join('')
}

<footer>${getReportFooterText()}</footer>
</body>
</html>`;
}
